const getDeviceJwt = () => {
  if (typeof window === 'undefined') return '';
  return String(window.localStorage.getItem('deviceJWT') || '').trim();
};

const storeRefreshedDeviceJwt = (response) => {
  if (typeof window === 'undefined' || !response) return;
  const refreshedJwt = response.headers.get('x-device-jwt');
  if (refreshedJwt) {
    window.localStorage.setItem('deviceJWT', refreshedJwt);
  }
};

const buildError = async (response, fallbackMessage) => {
  let message = fallbackMessage;
  let payload = null;

  try {
    payload = await response.json();
    message = payload?.message || payload?.error || fallbackMessage;
  } catch (err) {
    message = fallbackMessage;
  }

  const error = new Error(message);
  error.status = response.status;
  error.payload = payload;
  return error;
};

async function deviceRemoteRequest(path, options = {}) {
  const jwt = getDeviceJwt();
  if (!jwt) {
    const error = new Error('Missing device access token');
    error.status = 403;
    throw error;
  }

  const { body, headers = {}, ...rest } = options;
  const isJsonBody = body !== undefined;
  const response = await fetch(`/api/remote-device${path}`, {
    ...rest,
    headers: {
      ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${jwt}`,
      ...headers,
    },
    body: isJsonBody ? JSON.stringify(body) : undefined,
  });

  storeRefreshedDeviceJwt(response);

  if (!response.ok) {
    throw await buildError(response, `Request failed (${response.status})`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : {};
}

export const getRemoteDeviceProfile = () =>
  deviceRemoteRequest('/me', { method: 'GET' });

export const verifyRemotePhone = ({ code, areaId }) =>
  deviceRemoteRequest('/phone-remotes/verify', { method: 'POST', body: { code, areaId } });

export const saveRemotePhone = ({ verificationCode, deviceName, areaId }) =>
  deviceRemoteRequest('/phone-remotes/save', { method: 'POST', body: { verificationCode, deviceName, areaId } });

export const mixRemoteTargetNow = (targetDeviceId) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/mix-now`, { method: 'POST', body: {} });

// Match the operation hook's response shape while retaining device-JWT auth and refresh handling.
async function boundedOperationRequest(path, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try { return { data: await deviceRemoteRequest(path, { ...options, signal: controller.signal }) }; }
  finally { clearTimeout(timer); }
}
export const remoteScreenOperationApi = {
  post: (path, body) => boundedOperationRequest(path, { method: 'POST', body }),
  get: (path) => boundedOperationRequest(path, { method: 'GET' }),
};

export const remoteSiteOperationApi = {
  post: (path, body) => boundedOperationRequest(path, { method: 'POST', body }),
  get: (path) => boundedOperationRequest(path, { method: 'GET' }),
};

export const listRemoteTargets = () =>
  deviceRemoteRequest('/targets', { method: 'GET' });

export const getRemoteEpg = (targetDeviceId, query, signal) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/epg?${new URLSearchParams(query)}`, { method: 'GET', signal });
export const searchRemoteEpg = (targetDeviceId, search, signal) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/epg/search?${new URLSearchParams({ search })}`, { method: 'GET', signal });
export const watchRemoteEpg = (targetDeviceId, body) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/epg/watch`, { method: 'POST', body });
export const getRemoteEpgOperation = (targetDeviceId, operationId) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/epg/operations/${operationId}`, { method: 'GET' });

export const getRemoteVenueSummary = (areaId) => {
  const params = new URLSearchParams();
  if (areaId) params.set('areaId', String(areaId));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return deviceRemoteRequest(`/venue-summary${suffix}`, { method: 'GET' });
};

export const runRemoteVenueGigAction = ({ occurrenceId, action, requestKey, includeWelcome = true }) =>
  deviceRemoteRequest(`/venue-gigs/${occurrenceId}/actions`, {
    method: 'POST',
    body: { action, requestKey, includeWelcome },
  });

export const retryRemoteVenueGigAction = ({ occurrenceId, actionId }) =>
  deviceRemoteRequest(`/venue-gigs/${occurrenceId}/actions/${actionId}/retry`, {
    method: 'POST',
  });

export const startRemoteIdentificationScan = (areaId) =>
  deviceRemoteRequest('/identify/start', {
    method: 'POST',
    body: { areaId },
  });

export const completeRemoteIdentificationScan = ({ sessionId, token }) =>
  deviceRemoteRequest('/identify/complete', {
    method: 'POST',
    body: { sessionId, token },
  });

export const cancelRemoteIdentificationScan = (sessionId) =>
  deviceRemoteRequest('/identify/cancel', {
    method: 'POST',
    body: { sessionId },
  });

export const getRemoteTarget = (targetDeviceId, options = {}) => {
  const params = new URLSearchParams();
  if (options.includeApps !== undefined) {
    params.set('includeApps', options.includeApps ? '1' : '0');
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return deviceRemoteRequest(`/targets/${targetDeviceId}${suffix}`, { method: 'GET' });
};

export const listRemoteLauncherAds = (targetDeviceId) =>
  deviceRemoteRequest(`/launcher-ads?targetDeviceId=${encodeURIComponent(targetDeviceId)}`, { method: 'GET' });

export const launchRemoteLauncherAd = (targetDeviceId, { kind, id, slot, logicalScreenId, pauseBackground }) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/launcher-ads`, {
    method: 'POST', body: { kind, id, slot, logicalScreenId, pauseBackground },
  });

export const clearRemoteLauncherAds = ({ targetDeviceId, logicalScreenId }) =>
  deviceRemoteRequest('/launcher-ads/clear', { method: 'POST', body: { targetDeviceId, logicalScreenId } });

export const listRemoteAreaLauncherContent = (areaId) =>
  deviceRemoteRequest(`/launcher-content?areaId=${encodeURIComponent(areaId)}`, { method: 'GET' });

export const executeRemoteAreaPlaylist = (targetDeviceId, playlistId) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/playlists/${playlistId}/execute`, { method: 'POST' });

export const listRemoteContentTargets = (targetDeviceId) =>
  deviceRemoteRequest(`/targets/${targetDeviceId}/content-targets`, { method: 'GET' });

export const getRemoteTargetSchedule = (targetDeviceId, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return deviceRemoteRequest(`/targets/${targetDeviceId}/schedule${suffix}`, { method: 'GET' });
};

export const getRemoteAreaScheduleTimeline = ({ areaId, start, end }) => {
  const params = new URLSearchParams({
    areaId: String(areaId),
    start,
    end,
  });
  return deviceRemoteRequest(`/schedule-timeline?${params.toString()}`, { method: 'GET' });
};

export const previewRemoteMediaLink = ({ targetDeviceId, input }) =>
  deviceRemoteRequest('/media-links/preview', {
    method: 'POST',
    body: { targetDeviceId, input },
  });

export const saveRemoteMediaToAreaPlaylist = ({ areaId, url }) =>
  deviceRemoteRequest('/media-links/save-playlist', {
    method: 'POST',
    body: { areaId, url },
  });

export const runRemoteMediaLinkAction = ({
  targetDeviceId,
  profileId,
  url,
  title,
  action,
}) =>
  deviceRemoteRequest('/media-links/action', {
    method: 'POST',
    body: { targetDeviceId, profileId, url, title, action },
  });

export const runRemoteMediaPlaybackIntent = ({
  action,
  areaId,
  idempotencyKey,
  logicalScreenId,
  title,
  url,
}) =>
  deviceRemoteRequest('/media-links/playback-intent', {
    method: 'POST',
    body: { action, areaId, idempotencyKey, logicalScreenId, title, url },
  });

export const executeRemoteContentTarget = (targetDeviceId, contentTargetId) =>
  deviceRemoteRequest(
    `/targets/${targetDeviceId}/content-targets/${contentTargetId}/execute`,
    { method: 'POST', body: {} }
  );

export const sendRemoteSocketCommand = (payload) =>
  deviceRemoteRequest('/command', {
    method: 'POST',
    body: payload,
  });

export const sendRemoteRoutedAction = (payload) =>
  deviceRemoteRequest('/routed-action', {
    method: 'POST',
    body: payload,
  });

export const sendRemoteAndroidTvAction = (payload) =>
  deviceRemoteRequest('/android-tv-action', {
    method: 'POST',
    body: payload,
  });

export const openRemoteAndroidBluetoothSettings = (payload) =>
  deviceRemoteRequest('/android-bluetooth-settings', {
    method: 'POST',
    body: payload,
  });

export const sendRemoteProtocolCommand = (payload) =>
  deviceRemoteRequest('/protocol-command', {
    method: 'POST',
    body: payload,
  });

export const sendRemoteAssistantCommand = (payload) =>
  deviceRemoteRequest('/assistant-command', {
    method: 'POST',
    body: payload,
  });

export const requestRemoteState = async (payload, { timeout } = {}) => {
  const controller = timeout ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeout) : null;
  try {
    return await deviceRemoteRequest('/state', {
      method: 'POST', body: payload, ...(controller ? { signal: controller.signal } : {}),
    });
  } finally { if (timer) clearTimeout(timer); }
};

const buildLiveExperienceQuery = ({ areaId, eventInstanceId } = {}) => {
  const params = new URLSearchParams();
  if (areaId) params.set('areaId', areaId);
  if (eventInstanceId) params.set('eventInstanceId', eventInstanceId);
  return params.toString() ? `?${params.toString()}` : '';
};

export const listRemoteLiveExperienceInstances = ({ areaId } = {}) =>
  deviceRemoteRequest(`/live-experience-instances${buildLiveExperienceQuery({ areaId })}`, { method: 'GET' });

export const getRemoteKaraokeRuntime = (options = {}) =>
  deviceRemoteRequest(`/karaoke-runtime${buildLiveExperienceQuery(options)}`, { method: 'GET' });

export const getRemoteJukeboxRuntime = (options = {}) =>
  deviceRemoteRequest(`/jukebox-runtime${buildLiveExperienceQuery(options)}`, { method: 'GET' });

export const searchRemoteKaraokeCatalog = ({ query, maxResults, areaId, eventInstanceId } = {}) => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (maxResults) params.set('maxResults', maxResults);
  if (areaId) params.set('areaId', areaId);
  if (eventInstanceId) params.set('eventInstanceId', eventInstanceId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return deviceRemoteRequest(`/karaoke-runtime/search${suffix}`, { method: 'GET' });
};

export const searchRemoteJukeboxCatalog = ({ query, maxResults, areaId, eventInstanceId } = {}) => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (maxResults) params.set('maxResults', maxResults);
  if (areaId) params.set('areaId', areaId);
  if (eventInstanceId) params.set('eventInstanceId', eventInstanceId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return deviceRemoteRequest(`/jukebox-runtime/search${suffix}`, { method: 'GET' });
};

export const forceRemoteKaraokeQueueItem = (item, options = {}) =>
  deviceRemoteRequest('/karaoke-runtime/force-queue', {
    method: 'POST',
    body: { item, ...options },
  });

export const forceRemoteJukeboxQueueItem = (item, options = {}) =>
  deviceRemoteRequest('/jukebox-runtime/force-queue', {
    method: 'POST',
    body: { item, ...options },
  });

export const runRemoteKaraokeRuntimeAction = (payload) =>
  deviceRemoteRequest('/karaoke-runtime/actions', {
    method: 'POST',
    body: payload || {},
  });

export const runRemoteJukeboxRuntimeAction = (payload) =>
  deviceRemoteRequest('/jukebox-runtime/actions', {
    method: 'POST',
    body: payload || {},
  });

export const launchRemoteKaraokeRuntime = (options = {}) =>
  deviceRemoteRequest('/karaoke-runtime/launch', { method: 'POST', body: options });

export const stopRemoteKaraokeRuntime = (options = {}) =>
  deviceRemoteRequest('/karaoke-runtime/stop', { method: 'POST', body: options });

export const launchRemoteJukeboxRuntime = (options = {}) =>
  deviceRemoteRequest('/jukebox-runtime/launch', { method: 'POST', body: options });

export const stopRemoteJukeboxRuntime = (options = {}) =>
  deviceRemoteRequest('/jukebox-runtime/stop', { method: 'POST', body: options });

export const getRemoteAssignedDestinations = (deviceId) =>
  deviceRemoteRequest(`/targets/${deviceId}/assigned-app-destinations`);
export const executeRemoteAssignedApp = (deviceId, target, confirmShared = false) =>
  deviceRemoteRequest(`/targets/${deviceId}/assigned-app-executions`, { method: 'POST', body: {
    target, confirmShared, requestId: `remote-assigned-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  } });
export const getRemoteAssignedOperation = (deviceId, operationId) =>
  deviceRemoteRequest(`/targets/${deviceId}/assigned-app-operations/${operationId}`);

export const continueRemoteEpgOperation = (deviceId, operationId) => deviceRemoteRequest(`/targets/${deviceId}/epg/operations/${operationId}/continue`, { method: 'POST', body: {} });
export const cancelRemoteEpgOperation = (deviceId, operationId) => deviceRemoteRequest(`/targets/${deviceId}/epg/operations/${operationId}/cancel`, { method: 'POST', body: {} });

export const continueRemoteAssignedOperation = (deviceId, operationId) => deviceRemoteRequest(`/targets/${deviceId}/assigned-app-operations/${operationId}/continue`, { method: 'POST', body: {} });
export const cancelRemoteAssignedOperation = (deviceId, operationId) => deviceRemoteRequest(`/targets/${deviceId}/assigned-app-operations/${operationId}/cancel`, { method: 'POST', body: {} });

export const exportRemoteEpgOperation = (deviceId, operationId) => deviceRemoteRequest(`/targets/${deviceId}/epg/operations/${operationId}/export`);

export const getRemoteEpgOperationByRequest = (deviceId, requestId) => deviceRemoteRequest(`/targets/${deviceId}/epg/operations/by-request/${encodeURIComponent(requestId)}`);
